import {
  // validatePassword,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";
import { useContactStore } from '~/stores/contactStore';

export type UserDetails = {
  emailAddress: string;
  password: string;
};

export type SignupDetails = UserDetails & {
  humanChallengeToken: string;
};

export class PasswordValidationError extends Error {
  code: string;
  customError: boolean;

  constructor(message: string) {
    super(message);
    this.code = message;
    this.customError = true;
    this.name = "PasswordValidationError";
  }
}

/**
 * Get the Firebase Auth object from the Nuxt app.
 *
 * @returns {Auth} The Firebase Auth object.
 * @throws {Error} If the Firebase Auth object is not found.
 */
const getFirebaseAuth = () => {
  const nuxtApp = useNuxtApp();
  return nuxtApp.$auth as Auth;
};

const syncAuthenticatedUser = async (
  user: FirebaseUser,
  redirectTo: string,
) => {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  const idToken: string = await user.getIdToken();
  const idTokenCookie = useCookie("id_token", {
    sameSite: "lax",
    path: "/",
    secure: process.env.NODE_ENV === "production",
  });

  idTokenCookie.value = idToken;
  authStore.setIdToken(idToken);

  // Set a minimal user shape immediately so pages depending on `userId`
  // remain functional even if profile hydration is delayed or unavailable.
  // Full profile hydration, real-time notifications, and contact sync are
  // all owned by onAuthStateChanged in plugins/02.auth.client.ts, which
  // fires concurrently — calling getUserDetails() here too would duplicate
  // that API call on every sign-in.
  userStore.changeUser({
    ...userStore.userDetails,
    userId: user.uid,
    email: user.email ?? userStore.userDetails.email ?? "",
  });

  useRouter().push(redirectTo);
};

/**
 * Check if the password is valid
 *
 * @param password
 * @returns Promise<string>
 */
const isValidPassword = async (password: string) => {
  return new Promise((resolve, reject) => {
    const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$&*~])/;

    const isValid = regex.test(password);

    if (!isValid) {
      reject(
        new PasswordValidationError(
          "Password should contain at least : \nOne upper case\nOne lower case\nOne digit and one special character"
        )
      );
    } else {
      resolve("Password is valid.");
    }
  });
};

/**
 * Sign up a user
 *
 * @param userDetails
 * @returns Promise<any>
 */
export const signUp = async (userDetails: SignupDetails) => {
  await isValidPassword(userDetails.password);

  try {
    return await $fetch("/api/auth/signup", {
      method: "POST",
      body: {
        email: userDetails.emailAddress,
        password: userDetails.password,
        humanChallengeToken: userDetails.humanChallengeToken,
        source: {
          channel: "public_web",
          platform: "web",
          appVersion: useRuntimeConfig().public.appVersion || null,
          locale: navigator.language || null,
        },
      },
    });
  } catch (error) {
    if (typeof error === "object" && error !== null && "customError" in error) {
      throw error;
    }

    const message =
      (error as { statusMessage?: string }).statusMessage
      || (error as { data?: { message?: string } }).data?.message
      || "Unable to sign up.";

    throw {
      code: message,
      customError: true,
    };
  }
};

/**
 * Sign in a user
 *
 * @param userDetails
 * @returns Promise<void>
 */
export const signIn = async (userDetails: UserDetails, redirectTo: string) => {
  const auth = getFirebaseAuth();
  const { user } = await signInWithEmailAndPassword(
    auth,
    userDetails.emailAddress,
    userDetails.password
  );
  await syncAuthenticatedUser(user, redirectTo);
};

/**
 * Sign in or sign up a user with Google OAuth in the browser.
 */
export const signInWithGoogle = async (redirectTo = "/") => {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  const { user } = await signInWithPopup(auth, provider);
  await syncAuthenticatedUser(user, redirectTo);
};

/**
 * Send reset password email
 *
 * @param email
 * @returns Promise<void>
 */
export const sendResetPasswordEmail = async (email: string) => {
  const auth = getFirebaseAuth();
  await sendPasswordResetEmail(auth, email);
};

/**
 * Log out a user
 *
 * @returns Promise<void>
 */
export const logOut = async () => {
  try {
    // Clear decrypted contacts from IndexedDB before signing out to prevent
    // plaintext contact data being readable by the next browser user. The
    // session keypair is intentionally kept so the same user can re-login
    // without re-pairing; a different-user login is handled by initialize().
    await useContactStore().clearForLogout();

    await signOut(getFirebaseAuth());
    const idToken = useCookie("id_token", {
      sameSite: "lax",
      path: "/",
      secure: process.env.NODE_ENV === "production"
    });
    idToken.value = "";
    useAuthStore().clearIdToken();
    // Removed localStorage usage for userId
    useUserStore().changeUser({});
    useRouter().push("/auth/signin");
  } catch (error) {
    console.log(error);
  }
};
